import { useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, InputGroup, InputGroupText, FormFeedback, Button, Spinner, Row, Col, Progress } from 'reactstrap';
import { BiUpload } from 'react-icons/bi';
import { toast } from 'react-toastify';
import useCreateExpense from '../../hooks/useCreateExpense';
import useCreateExpenseFromReceipt from '../../hooks/useCreateExpenseFromReceipt';
import useDiscardScanJob from '../../hooks/useDiscardScanJob';
import useCategories, { CATEGORY_TARGET } from '../../hooks/useCategories';
import { queueReceiptScan } from '../../api/receiptsApi';
import { checkExpenseDuplicates } from '../../api/expensesApi';
import { currencySymbol, CURRENCIES } from '../../constants/currencies';
import ExpenseSplitEditor, { splitsToPayload, splitsHaveErrors } from './expenseSplitEditor';
import DuplicateWarning from '../common/DuplicateWarning';

// Mirror the server's gates (ReceiptController). Cheaper to fail at the
// browser than to stream a 9.9 MB upload the server will reject.
const MAX_BYTES = 10 * 1024 * 1024;
// Arbitrary sanity cap on one drop — stops "selected my Pictures folder by
// accident" from queuing 500 serial uploads.
const MAX_FILES = 20;
const ACCEPT_TYPES = 'image/*,application/pdf';

// Returns { errors: {amount, vatAmount, expenseDate}, hasErrors }.
// Kept inline (vs. a shared util like customerValidation) because expense
// validation is trivial and doesn't need to be shared with an autosave card
// yet — if/when inline-edit lands for expenses we'll lift this out.
function validateExpense({ amount, vatAmount, expenseDate }) {
    const errors = {};
    if (!expenseDate)                    errors.expenseDate = 'Expense date is required.';
    if (amount === '' || amount == null) errors.amount      = 'Amount is required.';
    else if (Number.isNaN(Number(amount)) || Number(amount) < 0)
                                         errors.amount      = 'Amount must be zero or positive.';
    if (vatAmount !== '' && vatAmount != null) {
        if (Number.isNaN(Number(vatAmount)) || Number(vatAmount) < 0)
            errors.vatAmount = 'VAT amount must be zero or positive.';
    }
    return { errors, hasErrors: Object.keys(errors).length > 0 };
}

const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

// Modal state machine for creating an expense.
//
//   intro     — drag-and-drop landing; offers "Enter manually instead" too.
//   uploading — serial multipart upload to QueueReceiptScan; shows a Progress
//               bar + "Uploading receipt X of Y…". OCR happens server-side
//               on the worker; the modal closes once the last upload returns.
//   manual    — plain form, used both via the "Enter manually" link and as
//               the "Fill manually" recovery target from the ScanJobsBanner
//               (in which case `pendingScanJob` carries the failed scan-job
//               row so we can commit via createExpenseFromReceipt and clean
//               the scan-job row up afterwards).
//
// Pending blobs in this flow live entirely on the server — the worker owns
// them. The modal never has to clean up after itself.
export default function CreateExpenseModal({ isOpen, onClose, jobId, currency = 'GBP', pendingScanJob = null }) {
    const createExpense = useCreateExpense();
    const createExpenseFromReceipt = useCreateExpenseFromReceipt();
    const discardScanJob = useDiscardScanJob();
    const categories = useCategories(CATEGORY_TARGET.Expense);

    // Mode drives which panel + footer is rendered. When `pendingScanJob` is
    // supplied we open straight into manual mode (Fill manually recovery).
    const [mode, setMode] = useState(pendingScanJob ? 'manual' : 'intro');
    const [uploadCount, setUploadCount] = useState(0);
    const [uploadTotal, setUploadTotal] = useState(0);
    const [dragActive, setDragActive] = useState(false);

    const [expenseDate, setExpenseDate] = useState(todayLocal());
    const [supplier, setSupplier]       = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory]       = useState('');
    const [amount, setAmount]           = useState('');
    const [vatAmount, setVatAmount]     = useState('');
    // Per-expense currency, prefilled from the job; its splits share it.
    const [currencyValue, setCurrencyValue] = useState(currency);
    const [splits, setSplits]           = useState([]);
    const [attempted, setAttempted]     = useState(false);
    // Likely duplicates from the pre-save check. Non-empty → show the warning
    // and swap the footer to "Save anyway"; the check never blocks the save.
    const [dupMatches, setDupMatches]   = useState([]);
    const [dupChecking, setDupChecking] = useState(false);

    const fileInputRef = useRef(null);

    const { errors, hasErrors } = validateExpense({ amount, vatAmount, expenseDate });

    // Re-sync mode + file fields whenever the modal is (re)opened with a
    // different pendingScanJob — useState only seeds on first mount.
    useEffect(() => {
        if (!isOpen) return;
        setCurrencyValue(currency);   // re-sync to the job currency on (re)open
        if (pendingScanJob) {
            setMode('manual');
            setSupplier(pendingScanJob.originalFileName ? '' : '');
        } else {
            setMode('intro');
        }
    }, [isOpen, pendingScanJob, currency]);

    const resetFormFields = () => {
        setExpenseDate(todayLocal());
        setSupplier(''); setDescription(''); setCategory('');
        setAmount(''); setVatAmount('');
        setCurrencyValue(currency);
        setSplits([]);
        setAttempted(false);
        setDupMatches([]);
        setDupChecking(false);
    };
    const resetAll = () => {
        setMode(pendingScanJob ? 'manual' : 'intro');
        setUploadCount(0); setUploadTotal(0);
        setDragActive(false);
        resetFormFields();
    };

    const handleClose = () => {
        // Nothing to clean up — pending blobs are owned by the worker and the
        // queue endpoint commits them as soon as the multipart finishes.
        resetAll();
        onClose();
    };

    // ---- Intro: drag-and-drop / file picker → serial QueueReceiptScan ----

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        if (files.length > MAX_FILES) {
            toast.error(`Too many files (max ${MAX_FILES}).`);
            return;
        }
        for (const f of files) {
            if (f.size > MAX_BYTES) {
                toast.error(`"${f.name}" is too large (max 10 MB).`);
                return;
            }
        }

        setMode('uploading');
        setUploadTotal(files.length);
        setUploadCount(0);

        let succeeded = 0;
        for (let i = 0; i < files.length; i++) {
            try {
                // Serial (not Promise.all) so the progress bar advances in a
                // visible step pattern and we don't hammer the server with
                // 20 concurrent multiparts. The real OCR work runs on the
                // worker after this returns.
                await queueReceiptScan(jobId, files[i]);
                succeeded += 1;
                setUploadCount(succeeded);
            } catch (err) {
                toast.error(err?.message ?? `Failed to upload "${files[i].name}"`);
                // A 403 means the scan limit has been reached — no point
                // attempting the remaining files since they'll all fail too.
                if (err?.status === 403) break;
                // Otherwise continue — one bad multipart shouldn't abort the
                // whole drop. The banner won't show a row for the failed
                // upload (it never reached the queue).
            }
        }

        if (succeeded > 0) {
            // Silent — rows appearing in the table is confirmation enough.
        }
        resetAll();
        onClose();
    };

    const handlePick = (e) => {
        // Snapshot before resetting `value` — the FileList from e.target.files
        // is a live reference, and clearing `value` empties it in-place, so
        // handleFiles would see zero files and bail silently.
        const files = Array.from(e.target.files || []);
        // Reset so the same filename can be re-picked after an error —
        // otherwise onChange won't fire because `files` is unchanged.
        e.target.value = '';
        handleFiles(files);
    };
    const onDragOver  = (e) => { e.preventDefault(); setDragActive(true); };
    const onDragLeave = (e) => { e.preventDefault(); setDragActive(false); };
    const onDrop      = (e) => {
        e.preventDefault();
        setDragActive(false);
        handleFiles(e.dataTransfer?.files);
    };

    // ---- Manual submit: plain form OR fill-manually recovery commit ----

    // Splits are gated on submit alongside the main form errors. The editor
    // shows its own balance UI; this is just the "block save" backstop.
    const splitsInvalid = splitsHaveErrors(splits, amount);
    const splitsPayload = splitsToPayload(splits);

    const busy = createExpense.isPending || createExpenseFromReceipt.isPending;

    // First submit runs the duplicate check; if it surfaces matches we show the
    // warning and stop, letting the user "Save anyway". A failed check never
    // blocks the save (best-effort warning, not a gate).
    const submitManual = async (e) => {
        e.preventDefault();
        if (busy || dupChecking) return;
        if (hasErrors || splitsInvalid) { setAttempted(true); return; }

        setDupChecking(true);
        let matches = [];
        try {
            matches = await checkExpenseDuplicates({
                amount: Number(amount),
                date: expenseDate,
                supplier: supplier.trim() || undefined,
            });
        } catch { /* ignore — warning is advisory */ }
        setDupChecking(false);
        if (matches.length > 0) { setDupMatches(matches); return; }

        await doSave();
    };

    const doSave = async () => {
        if (busy) return;
        if (pendingScanJob) {
            // Fill-manually recovery: commit via createExpenseFromReceipt so
            // the failed scan job's pending blob becomes a real Receipt under
            // the new Expense, then drop the scan-job row so the banner clears.
            await createExpenseFromReceipt.mutateAsync({
                jobId,
                expenseDate,
                supplier:    supplier.trim()    || null,
                description: description.trim() || null,
                categoryId:  category === '' ? null : Number(category),
                amount:      Number(amount),
                vatAmount:   vatAmount === '' ? null : Number(vatAmount),
                currency:    currencyValue,
                splits:      splitsPayload,
                pendingBlobPath:  pendingScanJob.pendingBlobPath,
                originalFileName: pendingScanJob.originalFileName,
                contentType:      pendingScanJob.contentType,
                fileSizeByBytes:  pendingScanJob.fileSizeByBytes,
            });
            // The pending blob now belongs to the new Receipt; the scan-job
            // row no longer references anything live, so just remove it.
            // discardScanJob skips the blob delete for non-pending paths but
            // we pass the row id either way and let the server decide.
            await discardScanJob.mutateAsync(pendingScanJob.id).catch(() => { /* row gone is fine */ });
        } else {
            await createExpense.mutateAsync({
                jobId,
                expenseDate,
                supplier:    supplier.trim()    || null,
                description: description.trim() || null,
                categoryId:  category === '' ? null : Number(category),
                amount:      Number(amount),
                vatAmount:   vatAmount === '' ? null : Number(vatAmount),
                currency:    currencyValue,
                splits:      splitsPayload,
            });
        }
        resetAll();
        onClose();
    };

    // ---- Render helpers ----

    const formFields = (
        <>
            <DuplicateWarning matches={dupMatches} currency={currencyValue} />
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Expense Date *</Label>
                        <Input
                            type="date"
                            value={expenseDate}
                            onChange={(e) => setExpenseDate(e.target.value)}
                            invalid={attempted && !!errors.expenseDate}
                        />
                        <FormFeedback>{errors.expenseDate}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>Category</Label>
                        <Input
                            type="select"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            disabled={categories.isLoading}
                        >
                            <option value="">— Select category —</option>
                            {(categories.data ?? []).map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>
            <FormGroup>
                <Label>Supplier</Label>
                <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} />
            </FormGroup>
            <FormGroup>
                <Label>Description</Label>
                <Input
                    type="textarea"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </FormGroup>
            <FormGroup>
                <Label>Currency</Label>
                <Input
                    type="select"
                    value={currencyValue}
                    onChange={(e) => setCurrencyValue(e.target.value)}
                    style={{ maxWidth: 240 }}
                >
                    {CURRENCIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>
                    ))}
                </Input>
            </FormGroup>
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Amount *</Label>
                        <InputGroup>
                            <InputGroupText>{currencySymbol(currencyValue)}</InputGroupText>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                invalid={attempted && !!errors.amount}
                            />
                            <FormFeedback>{errors.amount}</FormFeedback>
                        </InputGroup>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>VAT Amount</Label>
                        <InputGroup>
                            <InputGroupText>{currencySymbol(currencyValue)}</InputGroupText>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={vatAmount}
                                onChange={(e) => setVatAmount(e.target.value)}
                                invalid={attempted && !!errors.vatAmount}
                            />
                            <FormFeedback>{errors.vatAmount}</FormFeedback>
                        </InputGroup>
                    </FormGroup>
                </Col>
            </Row>
            <ExpenseSplitEditor
                value={splits}
                onChange={setSplits}
                primaryJobId={jobId}
                amount={amount}
                vatAmount={vatAmount}
                currency={currencyValue}
                onAmountChange={setAmount}
                disabled={createExpense.isPending || createExpenseFromReceipt.isPending}
            />
        </>
    );

    const headerText = (() => {
        if (mode === 'uploading') return 'Uploading receipts…';
        if (mode === 'manual')    return pendingScanJob
            ? `Fill manually — ${pendingScanJob.originalFileName ?? 'receipt'}`
            : 'New Expense';
        return 'New Expense';
    })();

    const uploadPct = uploadTotal > 0 ? Math.round((uploadCount / uploadTotal) * 100) : 0;

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered backdrop="static" size="lg">
            <ModalHeader toggle={handleClose}>{headerText}</ModalHeader>

            {mode === 'intro' && (
                <>
                    <ModalBody>
                        <div
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            onClick={() => fileInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                            className={`border border-2 rounded d-flex flex-column align-items-center justify-content-center text-center p-5 ${dragActive ? 'border-primary bg-light' : 'border-secondary-subtle'}`}
                            style={{ cursor: 'pointer', minHeight: 220 }}
                        >
                            <BiUpload size={48} className="mb-2 text-secondary" />
                            <div className="fw-semibold">Drop receipts here</div>
                            <div className="text-muted small">
                                or click to browse — images or PDF, up to 10 MB each
                            </div>
                            <div className="text-muted small mt-2">
                                Receipts process in the background; rows appear in the table as each finishes.
                            </div>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            multiple
                            accept={ACCEPT_TYPES}
                            onChange={handlePick}
                        />
                        <div className="text-center mt-3">
                            <Button color="link" size="sm" onClick={() => { resetFormFields(); setMode('manual'); }}>
                                Enter manually instead
                            </Button>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={handleClose}>Cancel</Button>
                    </ModalFooter>
                </>
            )}

            {mode === 'uploading' && (
                <>
                    <ModalBody>
                        <div className="d-flex flex-column align-items-center justify-content-center py-4">
                            <div className="fw-semibold mb-2">
                                Uploading receipt {Math.min(uploadCount + 1, uploadTotal)} of {uploadTotal}…
                            </div>
                            <Progress value={uploadPct} className="w-100" style={{ height: 10 }} />
                            <div className="text-muted small mt-2">
                                The modal closes when uploads finish; OCR runs in the background.
                            </div>
                        </div>
                    </ModalBody>
                    {/* Footer intentionally omitted — closing mid-upload would leave
                        the loop running anyway; the user can wait the few seconds. */}
                </>
            )}

            {mode === 'manual' && (
                <Form onSubmit={submitManual} noValidate>
                    <ModalBody>{formFields}</ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={handleClose}
                                disabled={busy || dupChecking}>Cancel</Button>
                        {dupMatches.length > 0 ? (
                            <Button color="warning" type="button" onClick={doSave} disabled={busy}>
                                {busy && <Spinner size="sm" className="me-1" />}
                                Save anyway
                            </Button>
                        ) : (
                            <Button color="primary" type="submit" disabled={busy || dupChecking}>
                                {(busy || dupChecking) && <Spinner size="sm" className="me-1" />}
                                {pendingScanJob ? 'Save Expense' : 'Create'}
                            </Button>
                        )}
                    </ModalFooter>
                </Form>
            )}
        </Modal>
    );
}
